import { useEffect, useState } from "react";
import { editPlannedSession } from "../../api/trainingPlanApi";
import { formatDate } from "../../lib/coachingFormatters";
import {
  clonePrescriptionForEdit,
  editPrescriptionToPayload,
} from "@/lib/workoutPrescriptionFormatters";
import { PrescriptionStepsEditor } from "@/components/coaching/PrescriptionStepsEditor";
import { SessionPrescriptionView } from "@/components/coaching/SessionPrescriptionView";
import { notifyActivePlanChanged } from "@/lib/activePlanSync";

/**
 * Shared modal for athlete-editable planned sessions.
 *
 * Supports duration/difficulty tweaks and full warmup / main-set /
 * cool-down prescription editing on the same authoritative record used
 * by Calendar and Coach.
 */

const DIFFICULTY_OPTIONS = [
  { id: "AS_PLANNED", label: "As planned" },
  { id: "A_LITTLE_EASIER", label: "A little easier" },
  { id: "EASIER", label: "Easier" },
  { id: "MUCH_EASIER", label: "Much easier" },
];

export function PlannedSessionEditor({ session, onClose, onSaved }) {
  const [duration, setDuration] = useState(session?.duration_minutes ?? "");
  const [difficulty, setDifficulty] = useState(
    session?.difficulty_adjustment || "AS_PLANNED",
  );
  const [reason, setReason] = useState(session?.athlete_edit_reason || "");
  const [warmup, setWarmup] = useState([]);
  const [mainSet, setMainSet] = useState([]);
  const [cooldown, setCooldown] = useState([]);
  const [prescriptionDirty, setPrescriptionDirty] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cloned = clonePrescriptionForEdit(session);
    setDuration(session?.duration_minutes ?? "");
    setDifficulty(session?.difficulty_adjustment || "AS_PLANNED");
    setReason(session?.athlete_edit_reason || "");
    setWarmup(cloned.warmup);
    setMainSet(cloned.mainSet);
    setCooldown(cloned.cooldown);
    setPrescriptionDirty(false);
    setShowPreview(false);
    setError(null);
    // We only re-seed the editor when a different session is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.session_id]);

  if (!session) return null;

  const originalDuration =
    session.original_duration_minutes ?? session.duration_minutes ?? null;

  const previewSession = {
    ...session,
    warmup: warmup.map((step) => ({
      step_type: step.step_type,
      label: step.label,
      duration_seconds: step.duration_minutes
        ? Math.round(Number(step.duration_minutes) * 60)
        : null,
      intensity: step.intensity_text ? { description: step.intensity_text } : null,
      athlete_cue: step.athlete_cue || null,
      repeat_count: Number(step.repeat_count) || 1,
      substeps: [],
    })),
    main_set: mainSet.map((step) => ({
      step_type: step.step_type,
      label: step.label,
      duration_seconds: step.duration_minutes
        ? Math.round(Number(step.duration_minutes) * 60)
        : null,
      intensity: step.intensity_text ? { description: step.intensity_text } : null,
      athlete_cue: step.athlete_cue || null,
      repeat_count: Number(step.repeat_count) || 1,
      substeps: [],
    })),
    cooldown: cooldown.map((step) => ({
      step_type: step.step_type,
      label: step.label,
      duration_seconds: step.duration_minutes
        ? Math.round(Number(step.duration_minutes) * 60)
        : null,
      intensity: step.intensity_text ? { description: step.intensity_text } : null,
      athlete_cue: step.athlete_cue || null,
      repeat_count: Number(step.repeat_count) || 1,
      substeps: [],
    })),
    prescription_customized: true,
  };

  const handleResetPrescription = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await editPlannedSession(session.session_id, {
        resetPrescription: true,
      });
      if (updated) {
        onSaved?.(updated);
        notifyActivePlanChanged({ source: "planned-session-edit", sessionId: session.session_id });
        onClose?.();
      } else {
        setError("Could not reset the workout prescription.");
      }
    } catch (err) {
      setError(
        err?.planError?.message
          || err?.message
          || "Could not reset the workout prescription.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const parsedDuration = duration === "" || duration == null
        ? null
        : Number(duration);
      if (parsedDuration != null && !Number.isFinite(parsedDuration)) {
        setError("Enter a valid duration in minutes.");
        setSaving(false);
        return;
      }

      const prescriptionOverride = prescriptionDirty
        ? editPrescriptionToPayload({ warmup, mainSet, cooldown })
        : null;
      if (prescriptionOverride) {
        const hasSteps = prescriptionOverride.warmup.length
          || prescriptionOverride.main_set.length
          || prescriptionOverride.cooldown.length;
        if (!hasSteps) {
          setError("Add at least one warm-up, main-set, or cool-down step.");
          setSaving(false);
          return;
        }
      }

      const updated = await editPlannedSession(session.session_id, {
        durationMinutes: parsedDuration ?? undefined,
        difficulty,
        reason: reason.trim() ? reason.trim() : null,
        prescriptionOverride: prescriptionOverride || undefined,
      });
      if (updated) {
        onSaved?.(updated);
        notifyActivePlanChanged({ source: "planned-session-edit", sessionId: session.session_id });
        onClose?.();
      } else {
        setError("Could not save your changes.");
      }
    } catch (err) {
      setError(
        err?.planError?.message
          || err?.message
          || "Could not save your changes.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="planned-session-editor"
    >
      <div className="flex max-h-[92vh] w-full max-w-3xl flex-col border border-dark-border bg-dark-surface-1">
        <div className="border-b border-dark-border p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-dark-text-muted">
            Edit workout
          </div>
          <div className="mt-1 text-lg font-heading tracking-wider text-dark-text-primary">
            {session.workout_name || session.workout_code || "Planned session"}
          </div>
          <div className="text-xs text-dark-text-muted">
            {session.sport ? `${session.sport} · ` : ""}
            {session.scheduled_date
              ? formatDate(session.scheduled_date)
              : "Upcoming"}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1 block text-dark-text-secondary">
                Duration (minutes)
              </span>
              <input
                type="number"
                min="5"
                max={originalDuration ?? 600}
                step="1"
                value={duration ?? ""}
                onChange={(e) => setDuration(e.target.value)}
                className="w-32 border border-dark-border bg-dark-background px-2 py-1 font-mono text-sm text-dark-text-primary"
                data-testid="planned-session-editor-duration"
              />
              {originalDuration != null && (
                <span className="ml-2 text-xs text-dark-text-muted">
                  max {originalDuration} (original)
                </span>
              )}
            </label>

            <label className="block text-sm sm:col-span-2">
              <span className="mb-1 block text-dark-text-secondary">
                Reason (optional)
              </span>
              <input
                type="text"
                maxLength={500}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Busy work day / My legs are tired / Only have 45 min"
                className="w-full border border-dark-border bg-dark-background px-2 py-1 text-sm text-dark-text-primary"
                data-testid="planned-session-editor-reason"
              />
            </label>
          </div>

          <fieldset data-testid="planned-session-editor-difficulty">
            <legend className="mb-1 text-sm text-dark-text-secondary">
              Quick difficulty
            </legend>
            <p className="mb-2 text-xs text-dark-text-muted">
              Use this when you only want to soften the coach targets. Editing
              the steps below replaces the full workout structure.
            </p>
            <div className="flex flex-wrap gap-2">
              {DIFFICULTY_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDifficulty(opt.id)}
                  data-testid={`planned-session-editor-difficulty-${opt.id}`}
                  data-selected={difficulty === opt.id ? "true" : "false"}
                  className={`rounded-md border px-3 py-1 text-xs ${
                    difficulty === opt.id
                      ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
                      : "border-dark-border text-dark-text-primary"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </fieldset>

          <div className="border-t border-dark-border pt-5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm text-dark-text-primary">Full workout plan</div>
                <p className="text-xs text-dark-text-muted">
                  Edit warm-up, main set, and cool-down step by step.
                </p>
              </div>
              <div className="flex gap-2">
                {session.prescription_customized && (
                  <button
                    type="button"
                    onClick={handleResetPrescription}
                    disabled={saving}
                    className="border border-dark-border px-2 py-1 text-[10px] uppercase tracking-widest text-dark-text-secondary"
                    data-testid="planned-session-editor-reset-prescription"
                  >
                    Reset to coach plan
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowPreview((current) => !current)}
                  className="border border-dark-border px-2 py-1 text-[10px] uppercase tracking-widest text-dark-text-secondary"
                  data-testid="planned-session-editor-toggle-preview"
                >
                  {showPreview ? "Hide preview" : "Preview"}
                </button>
              </div>
            </div>

            {showPreview ? (
              <SessionPrescriptionView
                session={previewSession}
                testId="planned-session-editor-preview"
              />
            ) : (
              <PrescriptionStepsEditor
                warmup={warmup}
                mainSet={mainSet}
                cooldown={cooldown}
                onWarmupChange={(next) => {
                  setWarmup(next);
                  setPrescriptionDirty(true);
                }}
                onMainSetChange={(next) => {
                  setMainSet(next);
                  setPrescriptionDirty(true);
                }}
                onCooldownChange={(next) => {
                  setCooldown(next);
                  setPrescriptionDirty(true);
                }}
              />
            )}
          </div>

          {error && (
            <p
              className="text-xs text-red-400"
              data-testid="planned-session-editor-error"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-dark-border p-5">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="border border-dark-border px-3 py-1 text-xs uppercase tracking-widest text-dark-text-secondary"
            data-testid="planned-session-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="border border-brand-primary bg-brand-primary/10 px-3 py-1 text-xs uppercase tracking-widest text-brand-primary"
            data-testid="planned-session-editor-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PlannedSessionEditor;
